import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';

import { useCustomer } from '../../../../context';

// ─────────────────────────────────────────────────────────────────────────────

export const useGroupOrderJoinForm = () => {
  const { customer } = useCustomer();
  const { formatMessage } = useIntl();

  // ─── Derived ─────────────────────────────────────────────────────────

  const customerName = (customer.firstName ?? '').slice(0, FIELD_MAX_LENGTH).trim();

  const rules = useMemo(
    () => ({
      customerName: {
        required: formatMessage(messages.customerNameRequired),
        maxLength: {
          value: FIELD_MAX_LENGTH,
          message: formatMessage(messages.customerNameMaxLength, { max_length: FIELD_MAX_LENGTH }),
        },
      },
    }),
    [formatMessage],
  );

  const form = useForm<GroupOrderJoinFormData>({
    defaultValues: { customerName },
    mode: 'onChange',
  });

  // ─────────────────────────────────────────────────────────────────────

  return {
    form,
    rules,
    maxLengths: { customerName: FIELD_MAX_LENGTH },
  };
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly customerNameRequired: NoMessageValues;
  readonly customerNameMaxLength: { readonly max_length: MessageValue };
}>({
  customerNameRequired: {
    defaultMessage: 'Name is required',
    description: 'Group Order Join | Name required error',
  },
  customerNameMaxLength: {
    defaultMessage: 'Name must be {max_length} characters or fewer',
    description: 'Group Order Join | Name max length error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Ops limit so participant names are not cut off on printed chits.
 */
const FIELD_MAX_LENGTH = 14;

// ─── Types ───────────────────────────────────────────────────────────────────

export type GroupOrderJoinFormData = {
  customerName: string;
};
